// -*- mode: jsx;-*-
/* global userObj */
/* eslint-disable react/prop-types */

import { Pagination } from '../common/pagination.js';
import { ProjectList } from '../common/ProjectList.js';
import { extractURLLocationParameters, makeSuggestionName, extractSuggestionLocationParameters } from '../common/locationUtils.js';
import { getPage } from '../common/requestUtils.js';
import { SmsSignupWidget, displaySignupWidget } from '../common/sms/SmsSignupWidget.js';
import TeacherList from './teacherList.js';
import SchoolList from './schoolList.js';
import { schoolSearchOnly, teacherSearchOnly, makeSearchURL } from '../common/searchRequestUtils.js';
import { extractBreadcrumbs } from './BreadcrumbUtils.js';
import AddSearchToChallengeLink from './AddSearchToChallengeLink.js';
import MatchingHeader from './MatchingHeader.js';
import { AjaxLink } from '../common/ajaxLink.js';
import { getURLParam, getURLParams } from '../adapters/dupes/queryParams.js';
import { encodeXml } from '../utils/encodeXML.ts';
import usePrimarySearchData from './usePrimarySearchData.js';
import { LocationExpansionMessage } from './LocationExpansionMessage.js';

const SearchResults = (props) => {
    const {
        callbacks,
        cart,
        donorGivingPages,
        isMobile,
        projects,
        schools,
        searchURL,
        showGiveWidget,
        teachers,
        user,
        getPerPage,
        baseSearchURL,
        detectedLocation,
    } = props;

    const { breadcrumb, resultList, totalResults, essentialsOnly, expandedLocationData } = usePrimarySearchData(projects);

    const makePagination = (max, isLoading) => {
        max = parseInt(max, 10);
        const page = getPage(searchURL);
        const isHistorical = new Uri(searchURL).hasQueryParam('historical', 'true');

        return <Pagination page={page} max={max} perPage={getPerPage()}
            makeURL={callbacks.makeSearchURL} changeURL={callbacks.changeURL}
            isLoading={isLoading} simplePagination={isMobile || isHistorical}
            url={searchURL}
        />;
    };
    const makeLocationExpansionMessage = () => {
        return (
            <LocationExpansionMessage
                callbacks={callbacks}
                expandedLocationData={expandedLocationData}
                searchURL={searchURL}
            />
        );
    };

    const makeNoResults = () => {
        const { searchURL, projects } = props;
        const params = getURLParams(searchURL);
        const keywords = encodeXml(params.keywords);

        const breadcrumbs = extractBreadcrumbs(breadcrumb, searchURL);
        const locationCrumb = breadcrumbs.filter(function (crumb) {
            return crumb.removeParams.includeNearbyLocations === null;
        })[0];

        const isSchoolSearch = schoolSearchOnly(searchURL);
        const isTeacherSearch = teacherSearchOnly(searchURL);

        const itemType = essentialsOnly ? 'classroom essentials' : 'projects';
        let heading;
        if (isSchoolSearch) {
            heading = `We weren't able to find any schools matching "${keywords}"`;
        } else if (isTeacherSearch) {
            heading = `We weren't able to find any teachers matching "${keywords}"`;
        } else if (locationCrumb) {
            heading = `We weren't able to find any ${itemType} in ` + locationCrumb.crumbName;
            if (keywords) {
                heading += ` matching "${keywords}"`;
            }
        } else if (params.school) {
            heading = projects.expandedLocationMessage;
        } else if (keywords) {
            heading = `We weren't able to find any ${itemType} matching "${keywords}"`;
        } else {
            heading = `We weren't able to find any matching ${itemType}`;
        }

        let suggestion;
        if (isTeacherSearch) {
            suggestion = <div>You may want to double-check your teacher&apos;s name or {makeStartOverLink()}</div>;
        } else if (isSchoolSearch) {
            suggestion =
                <div>You may want to double-check your school&apos;s name {makeProjectSearchLink(locationCrumb)}</div>;
        } else if (((!keywords || !locationCrumb) && breadcrumbs.length > 1) || breadcrumbs.length > 2) {
            suggestion = <div>Try removing some filters to see more results</div>;
        } else if (keywords) {
            suggestion = (
                <div>
                    {'You may want to '}
                    {makeStartOverLink()}
                    {makeHelperLinks(locationCrumb, keywords)}
                </div>
            );
        }

        return (
            <div className="no-results-message">
                <h1 dangerouslySetInnerHTML={{ __html: heading }}></h1>
                {suggestion}
            </div>
        );
    };

    const makeStartOverLink = () => {
        return (
            <AjaxLink href={baseSearchURL} handleLink={callbacks.changeURL}>
                start over
            </AjaxLink>
        );
    };

    const makeLocationBasedProjectSearchLink = (locationCrumb) => {
        let locationName, locationParams;

        if (locationCrumb) {
            locationName = locationCrumb.crumbName;
            locationParams = extractURLLocationParameters(searchURL);
            // extract location stuff
        } else if (detectedLocation) {
            locationName = makeSuggestionName(detectedLocation);
            locationParams = extractSuggestionLocationParameters(detectedLocation);
        }
        if (!locationName) {
            return;
        }
        return (
            <AjaxLink href={makeSearchURL(locationParams, baseSearchURL)}
                handleLink={callbacks.changeURL}>
                projects near {locationName}.
            </AjaxLink>
        );
    };

    const makeProjectSearchLink = (locationCrumb) => {
        const locationBasedProjectSearchLink = makeLocationBasedProjectSearchLink(locationCrumb);
        if (locationBasedProjectSearchLink) {
            return (
                <span>
                    {' or browse '}
                    { locationBasedProjectSearchLink }
                </span>
            );
        }
    };

    const makeTeacherSearchURL = (params) => {
        params.teachersOnly = true;
        const teacherSearchUrl = makeSearchURL(params, baseSearchURL);
        delete params.teachersOnly;
        return teacherSearchUrl;
    };

    const makeSchoolSearchURL = (params) => {
        params.schoolsOnly = true;
        const schoolSearchUrl = makeSearchURL(params, baseSearchURL);
        delete params.schoolsOnly;
        return schoolSearchUrl;
    };

    const makeHelperLinks = (locationCrumb, keywords) => {
        const locationBasedProjectSearchLink = makeLocationBasedProjectSearchLink(locationCrumb, keywords);
        if (!locationBasedProjectSearchLink) {
            return;
        }

        let locationName, params;
        if (locationCrumb) {
            locationName = locationCrumb.crumbName;
            params = extractURLLocationParameters(searchURL);
        } else if (detectedLocation) {
            locationName = makeSuggestionName(detectedLocation);
            params = extractSuggestionLocationParameters(detectedLocation);
        } else if (!keywords) {
            // If there was no keywords to search, do not include any other suggested search links
            return (
                <span>
                    {' or browse '}
                    { locationBasedProjectSearchLink }
                </span>
            );
        }

        if (!locationName) {
            return;
        }

        params.keywords = keywords;

        const helperLinks = [' or browse ', locationBasedProjectSearchLink];

        // If keywords includes common prefixes, show the teacher helper link, but not the school link
        const includeTeacherSearchLinkOnly = keywords && keywords.includes && /(teacher)|(mr\.)|(mrs\.)|(mx\.)/i.test(keywords);

        // If someone searched for "school" as part of their keywords, show the schoolLink, but not the teacher search link
        const includeSchoolSearchLinkOnly = keywords && keywords.includes && /school/i.test(keywords);

        // If there were some keywords, and both conditions are met, or neither are met, just show both links
        const includeBothTeacherAndSchoolSearchLinks = keywords && (includeSchoolSearchLinkOnly === includeTeacherSearchLinkOnly);

        if (includeBothTeacherAndSchoolSearchLinks || includeTeacherSearchLinkOnly) {
            helperLinks.push(' Were you searching for your ');
            helperLinks.push(
                <AjaxLink href={makeTeacherSearchURL(params, keywords)}
                    handleLink={callbacks.changeURL}>
                    favorite teacher?
                </AjaxLink>,
            );
        }

        if (includeBothTeacherAndSchoolSearchLinks || includeSchoolSearchLinkOnly) {
            const schoolConnectorText = includeBothTeacherAndSchoolSearchLinks
                ? ' Or a '
                : ' Were you searching for a ';
            helperLinks.push(schoolConnectorText);
            helperLinks.push(
                <AjaxLink href={makeSchoolSearchURL(params, keywords)}
                    handleLink={callbacks.changeURL}>
                    specific school?
                </AjaxLink>,
            );
        }

        return (
            <span>
                { helperLinks }
            </span>
        );
    };

    const makeTeacherList = (teachers) => {
        if (teachers.length > 0) {
            return <TeacherList {...props} />;
        }
    };

    const makeSchoolList = (schools) => {
        if (schools.length > 0) {
            return <SchoolList {...props} />;
        }
    };

    const makeProjectsHeader = (teachers, schools) => {
        if (teachers.length > 0 && schools.length > 0) {
            return <h3>Related Projects</h3>;
        } else {
            return null;
        }
    };

    const getCounts = (results) => {
        return results[0] && results[0].totalResults;
    };

    const renderPromotionPanel = () => {

        // Prioritize collecting SMS if they're logged in and haven't already opted in
        if (displaySignupWidget(user)) {
            return <SmsSignupWidget user={user} optInUpdateMethod={'SEARCH'} />;
        }

        if(!user && !getURLParam(searchURL, 'essentialsListsOnly')) {
            return (
                <div className="classroom-essentials-callout">
                    <div className="container">
                        <div className="essentials-content">
                            <div className="supply-side">
                                <div className="animated-supply">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                                <div className="badge"></div>
                            </div>
                            <div className="text-content">
                                <h1>Give teachers the <span className="essentials-color">basic supplies</span> they need most.</h1>
                                <a className="button-primary essentials"
                                   href="/donors/search.html?essentialsListsOnly=true">Find a teacher</a>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // only show panel if no logged in user, or logged in user does not have an active recurring donation
        if (!user || user.hasActiveRecurringDonation === false) {
            return (
                <div className="monthly-callout">
                    <h2>Like what you&rsquo;re seeing? Support classrooms monthly!</h2>
                    <p>
                        Sign up to automatically donate once a month, then <strong>handpick the projects</strong> you&nbsp;want&nbsp;to&nbsp;support.
                    </p>
                    <a className="button-secondary" href={baseURL + 'donors/recurring_donation_landing.html'}>Support classrooms monthly</a>
                </div>
            );
        }

        if (user.hasActiveRecurringDonation === true && (userObj.isTTD === true || userObj.isVIP === true)) {
            return (
                <section className="wtg-banner">

                    <div className="container">
                        <div className="image" role="img" aria-label="icons of different options for giving"></div>
                        <div className="inside-container">
                            <h3>There are plenty of ways you can support your favorite classrooms.</h3>
                            <p>From Donor-advised funds, donating a stock or using cryptocurrency, you have great options.</p>
                            <a href={baseURL + '../about/ways-to-give.html'} className="button-secondary small">See what works for you</a>
                        </div>
                    </div>

                </section>
            );
        }
    };

    const proposalList = projects.proposals || [];
    const quickDonateLink = baseURL + 'credits/';
    const mainMatchingFund = projects.mainMatchingFund || null;
    const isLoading = callbacks.isLoading();

    let pagination, noResults;
    if (resultList.length > 0 || teachers.length > 0 || schools.length > 0) {
        const total = totalResults || getCounts(teachers) || getCounts(schools);
        pagination = makePagination(total, isLoading);
    } else if (!isLoading) {
        noResults = makeNoResults();
    }

    const matchingId = getURLParam(searchURL, 'matchingId');
    let matching;
    if (matchingId > 0 && proposalList.length > 0) {
        if (mainMatchingFund && !isLoading) {
            matching = <MatchingHeader isMobile={isMobile} matchingFund={mainMatchingFund} />;
        }
    }

    // CC Comment: brining back for 9.7
    const isLoggedInTeacher = user && user.userType === 'teacher';

    return (
        <div className="search-results">
            {makeTeacherList(teachers)}
            {makeSchoolList(schools)}
            {makeLocationExpansionMessage()}
            {noResults}
            {matching}
            {makeProjectsHeader(teachers, schools)}
            { isLoggedInTeacher ? <div><div className="cartcopy-container desktop"><span>Teachers, you can copy most project carts when you&apos;re browsing! Just select &ldquo;Copy this cart.&rdquo;</span> <a className="cc-banner-link" href={`${baseURL}donors/search.html?solrQuery=approveddate:[now-90d/d%20TO%20now]`}>See all project carts you can copy</a></div></div> : '' }
            <ProjectList projects={proposalList} searchURL={searchURL}
                donorGivingPages={donorGivingPages} showGiveWidget={showGiveWidget}
                cart={cart} mainMatch={mainMatchingFund}
                breadcrumb={breadcrumb}
            />
            <div className="mobile-quick-donate-search show-quick-donate" style={{ display: 'none' }}>
                <h4>Don&apos;t want to pick a project?</h4>
                <p>You can still help support classrooms in need!</p>
                <a href={quickDonateLink} className="button-secondary">Donate now</a>
            </div>
            <div></div>
            {pagination}
            {renderPromotionPanel()}

            <div style={{ width: '100%', textAlign: 'right' }}>
                <AddSearchToChallengeLink donorGivingPages={donorGivingPages} searchURL={searchURL} />
            </div>
        </div>
    );
};

export default SearchResults;
